import { createReducer } from 'redux-ramda'
import {
  always,
  assoc,
  assocPath,
  path,
  pipe,
  applySpec,
  prop,
  invoker,
  flip,
  merge,
  mergeWith,
  concat,
  propOr,
  compose,
  complement,
  eqBy,
  identity,
  filter,
  over,
  lensPath,
  inc,
  pathOr,
} from 'ramda'
import { createAction } from 'redux-actions'
import { Map } from 'immutable'

import { ACTION_INIT } from './commonActions'
import { withSideEffects } from '~/redux/reduxSideEffects'
import { ACTION_SETTINGS_LOADED } from '~/redux/settingsReducer'
import { mergeCustomStyleMap, defaultCustomColors } from './customColors'

const ansContentSpecApplyer = (ansType, key, data) =>
  applySpec({
    [ansType]: pipe(
      prop(ansType),
      filter(pipe((item) => item.get('key'), complement(eqBy(identity)(key))))
    ),
  })(data)

const typePrefix = 'ellipsis.draftEditor'

export const actionTypes = {
  ACTION_HEADLINE_CONTENT: `${typePrefix}.headlineContent`,
  ACTION_CHARMBAR_CONTENT: `${typePrefix}.charmBarContent`,
  ACTION_EDITOR_CONTENT: `${typePrefix}.editorContent`,
  ACTION_SET_RELATED_CONTENT: `${typePrefix}.relatedContent`,
  ACTION_DENORMALIZE_RELATED_CONTENT: `${typePrefix}.denormalizeRelatedContent`,
  ACTION_EDITOR_CONTENT_UPDATE: `${typePrefix}.editorContentUpdate`,
  ACTION_HEADLINE_DRAFT_CONTENT: `${typePrefix}.headlineDraftContent`,
  ACTION_CLEAR_HEADLINE_CONTENT_STATE: `${typePrefix}.clearHeadlineContentState`,
  ACTION_HEADLINE_CONTENT_STATE: `${typePrefix}.headlineContentState`, // With my Browser Extention I saw tha this TYPE is called
  ACTION_SUBHEADLINE_DRAFT_CONTENT: `${typePrefix}.subheadlineDraftContent`,
  ACTION_SAVE: `${typePrefix}.saveStory`,
  ACTION_SYNC_EDITOR_CONTENT: `${typePrefix}.syncEditorContent`,
  ACTION_CLEAR_SUBHEADLINE_CONTENT_STATE: `${typePrefix}.clearSubheadlineContentState`,
  ACTION_SUBHEADLINE_CONTENT_STATE: `${typePrefix}.subheadlineContentState`,
  ACTION_CHANNELS: `${typePrefix}.channels`,
  ACTION_RATING_TYPES: `${typePrefix}.ratingTypes`,
  ACTION_TOOLBOX_SETTINGS: `${typePrefix}.toolbox`,
  ACTION_ADD_ANS_CLIPBOARD: `${typePrefix}.addANSClipboard`,
  ACTION_REMOVE_ANS_CLIPBOARD: `${typePrefix}.removeANSClipboard`,
  ACTION_ADD_RELATED_ITEM: `${typePrefix}.addRelatedItem`,
  ACTION_ADD_RELATED_ITEM_SUCCESS: `${typePrefix}.addRelatedItem-success`,
  ACTION_UPDATE_RELATED_ITEM_SUCCESS: `${typePrefix}.updateRelatedItem-success`,
  ACTION_REMOVE_RELATED_ITEM: `${typePrefix}.removeRelatedItem`,
  ACTION_COLORS_UPDATED: `${typePrefix}.customColors.updated`,
  ACTION_STYLES_UPDATED: `${typePrefix}.styleMap.updated`,
}

export const setHeadlineContent = createAction(
  actionTypes.ACTION_HEADLINE_CONTENT
)

export const setCharmBarContent = createAction(
  actionTypes.ACTION_CHARMBAR_CONTENT
)

export const setEditorContentState = createAction(
  actionTypes.ACTION_EDITOR_CONTENT
)

export const setRelatedContent = createAction(
  actionTypes.ACTION_SET_RELATED_CONTENT
)

export const denormalizeRelatedContent = createAction(
  actionTypes.ACTION_DENORMALIZE_RELATED_CONTENT
)

// Set Editor Content State as user edit content
export const updateEditorContentState = createAction(
  actionTypes.ACTION_EDITOR_CONTENT_UPDATE
)

// Headline editor
const setHeadlineDraftContentBare = createAction(
  actionTypes.ACTION_HEADLINE_DRAFT_CONTENT
)
const clearHeadlineContentState = createAction(
  actionTypes.ACTION_CLEAR_HEADLINE_CONTENT_STATE
)
export const setHeadlineDraftContent = withSideEffects(
  setHeadlineDraftContentBare,
  clearHeadlineContentState
)

export const setCurrentHeadlineContentState = createAction(
  actionTypes.ACTION_HEADLINE_CONTENT_STATE
)

// Subheadline editor
export const setSubheadlineDraftContentBare = createAction(
  actionTypes.ACTION_SUBHEADLINE_DRAFT_CONTENT
)

const clearSubheadlineContentState = createAction(
  actionTypes.ACTION_CLEAR_SUBHEADLINE_CONTENT_STATE
)
export const setSubheadlineDraftContent = withSideEffects(
  setSubheadlineDraftContentBare,
  clearSubheadlineContentState
)
export const setCurrentSubheadlineContentState = createAction(
  actionTypes.ACTION_SUBHEADLINE_CONTENT_STATE
)

// Save and export content state to Ellipsis
export const saveAction = createAction(actionTypes.ACTION_SAVE)

export const syncEditorContent = createAction(
  actionTypes.ACTION_SYNC_EDITOR_CONTENT
)

// Ellipsis channel config
export const setChannels = createAction(actionTypes.ACTION_CHANNELS)

// Ellipsis rating types config
export const setRatingTypes = createAction(actionTypes.ACTION_RATING_TYPES)

// Ellipsis toolbox settings
export const setToolboxSettings = createAction(
  actionTypes.ACTION_TOOLBOX_SETTINGS
)

export const addToANSClipboard = createAction(
  actionTypes.ACTION_ADD_ANS_CLIPBOARD
)

export const removeFromANSClipboard = createAction(
  actionTypes.ACTION_REMOVE_ANS_CLIPBOARD
)

export const addRelatedItem = createAction(actionTypes.ACTION_ADD_RELATED_ITEM)
export const addRelatedItemSuccess = createAction(
  actionTypes.ACTION_ADD_RELATED_ITEM_SUCCESS
)
export const updateRelatedItemSuccess = createAction(
  actionTypes.ACTION_UPDATE_RELATED_ITEM_SUCCESS
)

export const removeRelatedItem = createAction(
  actionTypes.ACTION_REMOVE_RELATED_ITEM
)

export const updateCustomColors = createAction(
  actionTypes.ACTION_COLORS_UPDATED
)

export const updateStyles = createAction(actionTypes.ACTION_STYLES_UPDATED)

const initialState = {
  contentState: Map(),
  customStyleMap: Map(),
  headlineContentState: Map(),
  subheadlineContentState: Map(),
  clipboardContent: {},
  relatedContent: {
    data: {},
    isDenormalizing: false,
    hasError: false,
  },
  saveCounter: 0,
  customColors: defaultCustomColors,
  channels: [],
}

export const _mergeStyleData = (customColors, state) => {
  return merge(state, {
    customColors,
    customStyleMap: mergeCustomStyleMap(customColors, state.customStyleMap),
  })
}

export default createReducer(initialState, [
  [ACTION_INIT, () => always(initialState)],
  [
    actionTypes.ACTION_HEADLINE_CONTENT,
    pipe(
      applySpec({
        headlineContentState: pipe(
          prop('headlineDraftContent'),
          invoker(1, 'get')('contentState')
        ),
        subheadlineContentState: pipe(
          prop('subheadlineDraftContent'),
          invoker(1, 'get')('contentState')
        ),
      }),
      flip(merge)
    ),
  ],
  [
    actionTypes.ACTION_CHARMBAR_CONTENT,
    pipe(
      applySpec({
        clipboardContent: prop('clipboardContent'),
        relatedContent: {
          data: prop('relatedContent'),
          isDenormalizing: always(false),
          hasError: always(false),
        },
      }),
      flip(merge)
    ),
  ],
  // This action handler extract mainDraftContent from payload and
  // get {contentState, customStyleMap} from Immutable map.
  // customStyleMap also processed by mergeCustomStyleMap call with customColors from state
  [
    actionTypes.ACTION_EDITOR_CONTENT,
    (payload) => (state) =>
      compose(
        merge(state),
        applySpec({
          contentState: invoker(1, 'get')('contentState'),
          customStyleMap: compose(
            mergeCustomStyleMap(prop('customColors', state)),
            invoker(1, 'get')('customStyleMap')
          ),
        }),
        prop('mainDraftContent')
      )(payload),
  ],
  [
    actionTypes.ACTION_DENORMALIZE_RELATED_CONTENT,
    () => (state) =>
      assocPath(['relatedContent', 'isDenormalizing'], true, state),
  ],
  [actionTypes.ACTION_SET_RELATED_CONTENT, assoc('relatedContent')],
  [actionTypes.ACTION_EDITOR_CONTENT_UPDATE, assoc('contentState')],
  [actionTypes.ACTION_HEADLINE_CONTENT_STATE, assoc('headlineContentState')],
  [
    actionTypes.ACTION_SUBHEADLINE_CONTENT_STATE,
    assoc('subheadlineContentState'),
  ],
  [actionTypes.ACTION_CHANNELS, assoc('channels')],
  [actionTypes.ACTION_RATING_TYPES, assoc('ratingTypes')],
  [actionTypes.ACTION_TOOLBOX_SETTINGS, assoc('toolboxSettings')],
  [
    actionTypes.ACTION_ADD_ANS_CLIPBOARD,
    (payload) => (state) =>
      pipe(
        mergeWith(concat, propOr({}, 'clipboardContent', state)),
        assoc('clipboardContent')
      )(payload)(state),
  ],
  [
    actionTypes.ACTION_REMOVE_ANS_CLIPBOARD,
    (payload) => (state) =>
      assoc(
        'clipboardContent',
        merge(
          prop('clipboardContent', state),
          ansContentSpecApplyer(
            payload.type,
            payload.id,
            state.clipboardContent
          )
        ),
        state
      ),
  ],
  [
    actionTypes.ACTION_ADD_RELATED_ITEM_SUCCESS,
    (payload) => (state) =>
      pipe(
        mergeWith(concat, path(['relatedContent', 'data'], state)),
        assocPath(['relatedContent', 'data'])
      )(payload)(state),
  ],
  [
    actionTypes.ACTION_UPDATE_RELATED_ITEM_SUCCESS,
    (payload) => (state) => {
      return {
        ...state,
        relatedContent: {
          ...state.relatedContent,
          data: {
            ...state.relatedContent.data,
            /**
             * loop over data by type (at this point it should only be custom_embed
             * that triggers this action) and update entry if the _ids match
             */
            [payload.key]: state.relatedContent.data[payload.key].map((item) =>
              item.getIn(['clipboardANS', '_id']) ===
              payload.data.getIn(['clipboardANS', '_id'])
                ? payload.data
                : item
            ),
          },
        },
      }
    },
  ],
  [
    actionTypes.ACTION_REMOVE_RELATED_ITEM,
    (payload) => (state) =>
      assocPath(
        ['relatedContent', 'data'],
        merge(
          path(['relatedContent', 'data'], state),
          ansContentSpecApplyer(
            payload.type,
            payload.id,
            state.relatedContent.data
          )
        ),
        state
      ),
  ],
  [actionTypes.ACTION_SAVE, () => over(lensPath(['saveCounter']), inc)],
  // On settings load, store customColors from settings and merge customStyleMap from the state
  // ACTION_EDITOR_CONTENT also merge customStyleMap according to the existing customColors value
  [
    ACTION_SETTINGS_LOADED,
    (payload) => (state) =>
      _mergeStyleData(
        pathOr(defaultCustomColors, ['data', 'customColors'], payload),
        state
      ),
  ],
  [
    actionTypes.ACTION_COLORS_UPDATED,
    (payload) => (state) => _mergeStyleData(payload, state),
  ],
  [
    actionTypes.ACTION_STYLES_UPDATED,
    (payload) => (state) => ({
      ...state,
      customStyleMap: state.customStyleMap.set(
        payload.styleKey,
        payload.styles
      ),
    }),
  ],
])

export const getDraftEditorContentState = path(['draftEditor', 'contentState'])
export const getHeadlineCurrentContentState = path([
  'draftEditor',
  'headlineContentState',
])
export const getSubheadlineCurrentContentState = path([
  'draftEditor',
  'subheadlineContentState',
])
export const draftExportSelector = path(['draftEditor', 'exported'])
export const toolboxSettingsSelector = path(['draftEditor', 'toolboxSettings'])
export const clipboardContent = path(['draftEditor', 'clipboardContent'])
export const relatedContent = path(['draftEditor', 'relatedContent'])
export const saveCounter = path(['draftEditor', 'saveCounter'])
